import { useState } from 'react';
import { Copy, Download, Check } from 'lucide-react';
import ToolLayout from '../../components/dev-tools/ToolLayout';
import { copyToClipboard, downloadFile } from '../../utils/clipboard';

export default function Base64Decode() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const decodeBase64 = () => {
    try {
      const decoded = decodeURIComponent(escape(atob(input.trim())));
      setOutput(decoded);
      setError('');
    } catch (err) {
      setError('Invalid Base64 string. Please check your input.');
      setOutput('');
    }
  };

  const handleCopy = async () => {
    if (output) {
      const success = await copyToClipboard(output);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleDownload = () => {
    if (output) {
      downloadFile(output, 'decoded.txt', 'text/plain');
    }
  };

  return (
    <ToolLayout
      title="Base64 Decoder"
      description="Decode Base64 encoded strings back to plain text"
    >
      <div className="space-y-6">
        {/* Input */}
        <div className="card">
          <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-50">Base64 Input</h3>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Paste Base64 encoded string here..."
            className="mb-4 h-48 w-full rounded-lg border border-gray-300 bg-white p-4 font-mono text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500 dark:focus:border-primary-400"
          />

          <button onClick={decodeBase64} className="btn-primary">
            Decode
          </button>
        </div>

        {/* Output */}
        <div className="card">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-50">Decoded Output</h3>
            {output && (
              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                >
                  <Download size={16} />
                  Download
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
              <strong>Error:</strong> {error}
            </div>
          )}

          <pre className="h-48 w-full overflow-auto rounded-lg border border-gray-300 bg-gray-50 p-4 font-mono text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
            {output || 'Decoded output will appear here...'}
          </pre>
        </div>
      </div>
    </ToolLayout>
  );
}
